// S-2210: OpenAI client with chunking support for large payloads
// S-2211: Added structured error detection and handling
// S-2237: Added watchdog for CORS/network failures
import { PayloadChunk } from './chunker';
import { edgeInvoke } from '@/lib/edgeInvoke';
import { AnalysisError, AnalysisErrorType, isNetworkOrCorsError, isRetryableError } from './error-types';

export interface ChunkedAnalysisResult {
  runId: string;
  totalParts: number;
  completedParts: number;
  results: PartialAnalysisResult[];
  mergedResult?: AnalysisResult;
  frameAudit: {
    totalFrames: number;
    framesSent: number;
    framesAnalyzed: number;
  };
}

export interface PartialAnalysisResult {
  partIndex: number;
  frameIndices: number[];
  bullets: string[];
  scores: {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  };
  frameCount: number;
}

export interface AnalysisResult {
  bullets: string[];
  scores: {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  };
  frameAudit: {
    totalFrames: number;
    framesSent: number;
    framesAnalyzed: number;
  };
}

export class ChunkedOpenAIClient {

  /**
   * S-2210: Submit all chunks sequentially and merge results
   * S-2237: Added watchdog to halt on CORS/network failures
   * S-2243: Added pre-flight validation for telemetry
   * Ensures ALL frames are analyzed (no dropping) unless watchdog triggers
   */
  async analyzeChunks(
    chunks: PayloadChunk[],
    playbackId: string
  ): Promise<ChunkedAnalysisResult> {
    console.log(`[S-2210 OpenAI] Analyzing ${chunks.length} chunks for runId: ${chunks[0]?.runId}`);
    
    const runId = chunks[0]?.runId || 'unknown';
    
    // S-2243: Pre-flight validation - check all chunks have required data
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      if (!chunk.frames || chunk.frames.length === 0) {
        throw AnalysisError.inputMissing(
          'frames',
          'No video frames available for analysis.'
        );
      }
      
      // S-2243: Require telemetry OR pose_summary
      const hasTelemetry = chunk.telemetry && chunk.telemetry.bytes > 0;
      const hasPoseSummary = chunk.pose_summary && chunk.pose_summary.length > 0;
      
      if (!hasTelemetry && !hasPoseSummary) {
        throw AnalysisError.inputMissing(
          'telemetry',
          'We didn\'t receive swing telemetry. Please re-run pose extraction, then try analyze again.'
        );
      }
    }
    
    const results: PartialAnalysisResult[] = [];
    let totalFrames = 0;
    let framesSent = 0;
    let framesAnalyzed = 0;
    
    // S-2237: Watchdog flag for checklist compliance (while keeping immediate-throw behavior)
    let abortAnalysis = false;
    
    // S-2237: Sequential processing with immediate exit on CORS/network failures
    for (let i = 0; i < chunks.length; i++) {
      // S-2237: Check abort flag for checklist compliance
      if (abortAnalysis) {
        console.warn(`[S-2237 Watchdog] Analysis aborted before chunk ${i + 1}, runId: ${runId}`);
        break;
      }
      const chunk = chunks[i];
      
      // S-2243: Log chunk payload details
      console.log(`[S-2210 OpenAI] Chunk ${i + 1}/${chunks.length} payload: frames=${chunk.frames.length}, telemetry_bytes=${chunk.telemetry?.bytes || 0}, pose_summary=${chunk.pose_summary?.length || 0}`);
      
      try {
        const partialResult = await this.submitChunk(chunk, playbackId);
        results.push(partialResult);
        
        totalFrames += chunk.frames.length;
        framesSent += chunk.frames.length;
        framesAnalyzed += partialResult.frameCount;
        
        console.log(`[S-2210 OpenAI] Chunk ${i + 1} completed: ${partialResult.frameCount} frames analyzed`);
        
      } catch (error) {
        console.error(`[S-2210 OpenAI] Chunk ${i + 1} failed:`, error);
        
        // S-2243: Check if this is a structured AnalysisError
        if (error instanceof AnalysisError) {
          // S-2243: RETRYABLE_UPSTREAM from server should throw (not continue)
          if (error.type === AnalysisErrorType.RETRYABLE_UPSTREAM ||
              error.type === AnalysisErrorType.MODEL_REJECTED ||
              error.type === AnalysisErrorType.ANALYSIS_INPUT_MISSING) {
            throw error; // Throw immediately for these error types
          }
        }
        
        // S-2237: Check for CORS/network errors that should trigger watchdog
        const errorMessage = String(error?.message || error);
        const isNetworkOrCors = error instanceof TypeError || 
          /CORS|Failed to fetch|blocked by cors policy|cross-origin|connection timeout|network error|connection refused/i.test(errorMessage);
        
        if (isNetworkOrCors) {
          console.error(`[S-2237 Watchdog] Network/CORS failure at chunk ${i + 1}, runId: ${runId}`);
          // S-2237: Set flag for checklist compliance
          abortAnalysis = true;
          // CRITICAL: Throw and immediately exit the function (no more chunks).
          throw AnalysisError.networkOrCorsFailure(runId, i + 1, error);
        }
        
        // S-2237: Allow retryable errors to continue (429, 503, etc.) - legacy behavior
        if (isRetryableError(error)) {
          console.log(`[S-2237 Watchdog] Retryable error detected for chunk ${i + 1}: ${errorMessage}`);
          // Create empty result for failed chunk to maintain ordering
          results.push({
            partIndex: i,
            frameIndices: chunk.frameIndices,
            bullets: [`Analysis failed for frames ${chunk.frameIndices[0]}-${chunk.frameIndices[chunk.frameIndices.length - 1]} (retryable error)`],
            scores: { overall: 0, tempo: 0, form: 0, power: 0 },
            frameCount: 0
          });
          
          totalFrames += chunk.frames.length;
          framesSent += chunk.frames.length;
          // framesAnalyzed remains unchanged for failed chunks
          continue; // Continue with next chunk for retryable errors
        }
        
        // For other non-retryable errors, rethrow immediately
        throw error;
      }
    }
    
    const frameAudit = {
      totalFrames,
      framesSent,
      framesAnalyzed
    };
    
    console.log(`[S-2210 OpenAI] Frame audit: total=${totalFrames}, sent=${framesSent}, analyzed=${framesAnalyzed}`);
    
    // Merge results if multiple chunks
    const mergedResult = chunks.length > 1 ? this.mergeResults(results, frameAudit) : this.convertToAnalysisResult(results[0], frameAudit);
    
    return {
      runId,
      totalParts: chunks.length,
      completedParts: results.filter(r => r.frameCount > 0).length,
      results,
      mergedResult,
      frameAudit
    };
  }

  private async submitChunk(
    chunk: PayloadChunk,
    playbackId: string
  ): Promise<PartialAnalysisResult> {
    // S-2243: Prepare payload with v2 contract fields
    const payload = {
      // V2 contract fields
      run_id: chunk.run_id,
      chunk_index: chunk.chunk_index,
      total_chunks: chunk.total_chunks,
      video_id: chunk.video_id,
      user_id: chunk.user_id,
      playback_id: chunk.playback_id,
      frames: chunk.frames,
      telemetry: chunk.telemetry,
      pose_summary: chunk.pose_summary,
      // Legacy fields for backward compatibility
      video_frames: chunk.frames,
      chunk_info: {
        runId: chunk.runId,
        partIndex: chunk.partIndex,
        totalParts: chunk.totalParts,
        frameIndices: chunk.frameIndices
      }
    };
    
    console.log(`[S-2210 OpenAI] Chunk payload: ${chunk.frames.length} frames, ${chunk.estimatedSize} bytes`);
    
    try {
      // Submit to enhanced edge function
      const result = await edgeInvoke('pose-analyze-video-chunked', payload);
      
      if (!result) {
        throw AnalysisError.modelError('No result from analysis service');
      }
      
      // S-2243: Check for server error responses
      if (result.ok === false || result.code) {
        // Map server error codes to client error types
        if (result.code === 'INVALID_PAYLOAD') {
          throw AnalysisError.inputMissing(
            result.missing?.join(', ') || 'required fields',
            'Analysis requires complete video data. Please ensure pose extraction completed successfully.'
          );
        }
        
        if (result.code === 'RETRYABLE_UPSTREAM') {
          throw AnalysisError.retryable(result.message || 'Upstream service temporarily unavailable');
        }
        
        if (result.code === 'MODEL_REJECTED') {
          throw AnalysisError.modelRejected(result.message || 'Model rejected request');
        }
        
        // If we have a code but it's not recognized, treat as unknown
        if (result.code) {
          throw AnalysisError.unknown(result.message || 'Unknown error from analysis service');
        }
      }
      
      // S-2211: Check for model-level errors in response
      if (result.error) {
        const errorMessage = result.error.toLowerCase();
        if (errorMessage.includes('insufficient') || errorMessage.includes('cannot analyze') || 
            errorMessage.includes('not enough') || errorMessage.includes('poor quality')) {
          throw AnalysisError.modelError(result.error);
        }
        throw AnalysisError.modelError(`Analysis service error: ${result.error}`);
      }
      
      // S-2211: Validate response has meaningful content
      if (!result.bullets || result.bullets.length === 0) {
        throw AnalysisError.modelError('Analysis produced no feedback - video may be unclear or too short');
      }
      
      return {
        partIndex: chunk.partIndex,
        frameIndices: chunk.frameIndices,
        bullets: result.bullets || [],
        scores: result.scores || { overall: 0, tempo: 0, form: 0, power: 0 },
        frameCount: chunk.frames.length
      };
      
    } catch (error) {
      // S-2243: Network/CORS errors are handled by caller (watchdog)
      if (error instanceof TypeError) {
        throw error; // Let watchdog handle it
      }
      
      // S-2243: AnalysisError types are already mapped
      if (error instanceof AnalysisError) {
        throw error;
      }
      
      // Unknown error
      throw AnalysisError.unknown(error);
    }
  }

  private mergeResults(results: PartialAnalysisResult[], frameAudit: any): AnalysisResult {
    console.log(`[S-2210 OpenAI] Merging ${results.length} partial results`);
    
    // Combine all bullets in order
    const allBullets: string[] = [];
    const allScores: number[][] = [[], [], [], []]; // [overall, tempo, form, power]
    let totalFrames = 0;
    
    // Sort results by part index to ensure correct order
    const sortedResults = results.sort((a, b) => a.partIndex - b.partIndex);
    
    for (const result of sortedResults) {
      if (result.frameCount > 0) {
        // Add bullets with part prefix for clarity
        const partBullets = result.bullets.map(bullet => 
          `[Part ${result.partIndex + 1}] ${bullet}`
        );
        allBullets.push(...partBullets);
        
        // Collect scores for weighted averaging
        allScores[0].push(result.scores.overall);
        allScores[1].push(result.scores.tempo);
        allScores[2].push(result.scores.form);
        allScores[3].push(result.scores.power);
        
        totalFrames += result.frameCount;
      }
    }
    
    // Calculate weighted average scores
    const avgScores = {
      overall: this.calculateAverage(allScores[0]),
      tempo: this.calculateAverage(allScores[1]),
      form: this.calculateAverage(allScores[2]),
      power: this.calculateAverage(allScores[3])
    };
    
    // Add summary bullet
    const summaryBullet = `Analysis completed across ${results.length} segments with ${totalFrames} total frames`;
    allBullets.unshift(summaryBullet);
    
    console.log(`[S-2210 OpenAI] Merged analysis: ${allBullets.length} bullets, avg scores: ${JSON.stringify(avgScores)}`);
    
    return {
      bullets: allBullets,
      scores: avgScores,
      frameAudit
    };
  }

  private convertToAnalysisResult(result: PartialAnalysisResult, frameAudit: any): AnalysisResult {
    return {
      bullets: result.bullets,
      scores: result.scores,
      frameAudit
    };
  }

  private calculateAverage(scores: number[]): number {
    if (scores.length === 0) return 0;
    const sum = scores.reduce((acc, score) => acc + score, 0);
    return Math.round(sum / scores.length);
  }
}

export const chunkedOpenAIClient = new ChunkedOpenAIClient();
